import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import RewardsAPI from 'api/faber/RewardsAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const formikInitialValues = {
  catalogId: '',
  paymentIntent: ''
}

const NewUserPlanSchema = Yup.object().shape({
  paymentIntent: Yup.string()
    .matches(/^pi_/, 'Payment Intent must start with "pi_"')
    .required('Payment Intent is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

interface Props {
  handleClose: () => void
  userId: string
  iccid: string
}

export const NewUserPlanForm = ({ handleClose, userId, iccid}: Props) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data: catalogIds } = useQuery(['getCatalogs'], async () =>
    RewardsAPI.getCatalogs(faberToken)
  )

  const { mutate: createUserPlanByICCID } = useMutation(
    OneTimePackApi.createUserPlanByICCID,
    {
      onSuccess: () => {
        toast.success('User plan created successfully')
        handleClose()
      },
      onError: () => {
        toast.error('Error creating user plan')
      }
    }
  )

  interface CatalogRow {
    id: string
    name: string
    description: string
    tags: string[]
  }
  const [catalogRows, setCatalogRows] = React.useState<CatalogRow[]>([])
  const [catalogSelectionModel, setCatalogSelectionModel] =
    React.useState<string>('')
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200
    },
    {
      field: 'tenant',
      headerName: 'Tenant',
      width: 100
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 500
    },
    {
      field: 'packageType',
      headerName: 'Package Type',
      width: 120
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 200
    },
  ]

  useEffect(() => {
    if (catalogIds) {
      setCatalogRows(catalogIds)
    }
  }, [catalogIds])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewUserPlanFormikProps) => {
    const allValues = {
      userId: userId,
      catalogId: catalogSelectionModel,
      paymentIntent: values.paymentIntent,
      faberToken: faberToken,
      iccid: iccid
    }
    createUserPlanByICCID(allValues)
  }

  /**
   * Event handlers
   */

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewUserPlanSchema}
    >
      {({ submitForm, handleChange, errors, touched, handleBlur }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Catalog ID</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={catalogRows}
                columns={columns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={catalogSelectionModel}
                onSelectionModelChange={(newSelection) => {
                  setCatalogSelectionModel(newSelection[0] as string)
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Payment Intent</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'payment-intent'
                  }}
                  name='paymentIntent'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.paymentIntent && touched.paymentIntent)}
                  helperText={
                    errors.paymentIntent && touched.paymentIntent
                      ? errors.paymentIntent
                      : null
                  }
                />
              </Box>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={() => submitForm()}>Save changes</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewUserPlanFormikProps {
  catalogId: string
  paymentIntent: string 
}
